import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  DepartmentTree,
  DepartmentDetail,
  CreateDepartmentDialog,
  EditDepartmentDialog,
  AddUserDialog,
  DeleteDepartmentDialog,
} from '@/components/departments'
import { useDepartmentTree } from '@/hooks/useDepartments'
import { findDepartmentById } from '@/lib/departments'
import type { ApiDepartment } from '@/types/department'

export function DepartmentsPage() {
  const { deptId } = useParams<{ deptId?: string }>()
  const navigate = useNavigate()

  // 将 URL 中的 string 转换为 number
  const selectedDeptId = deptId ? parseInt(deptId, 10) : undefined
  const isValidDeptId = selectedDeptId && !isNaN(selectedDeptId)

  // 弹窗状态
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  // 当前操作的部门
  const [selectedParentId, setSelectedParentId] = useState<number | undefined>()
  const [editingDepartment, setEditingDepartment] = useState<ApiDepartment | null>(null)
  const [deletingDepartment, setDeletingDepartment] = useState<ApiDepartment | null>(null)

  // 获取部门树数据
  const { data: treeData } = useDepartmentTree()

  const selectedDepartment = isValidDeptId ? findDepartmentById(treeData, selectedDeptId) ?? null : null

  // 选择部门
  const handleSelectDepartment = (id: number) => {
    navigate(`/users/departments/${id}`)
  }

  // 新增顶级部门
  const handleAddRoot = () => {
    setSelectedParentId(undefined)
    setCreateDialogOpen(true)
  }

  // 新增子部门
  const handleAddChild = (parentId: number) => {
    setSelectedParentId(parentId)
    setCreateDialogOpen(true)
  }

  // 编辑部门
  const handleEdit = (dept: ApiDepartment) => {
    setEditingDepartment(dept)
    setEditDialogOpen(true)
  }

  // 删除部门
  const handleDelete = (dept: ApiDepartment) => {
    setDeletingDepartment(dept)
    setDeleteDialogOpen(true)
  }

  // 添加用户
  const handleAddUser = () => {
    setAddUserDialogOpen(true)
  }

  // 创建成功后清除 parentId
  const handleCreateSuccess = () => {
    setSelectedParentId(undefined)
  }

  // 删除成功后导航到部门列表
  const handleDeleteSuccess = () => {
    navigate('/users/departments')
    setDeletingDepartment(null)
  }

  return (
    <div className="flex flex-col lg:flex-row h-[calc(100vh-120px)]">
      {/* 部门树 */}
      <div className="w-full lg:w-80 shrink-0 max-h-[40vh] lg:max-h-full overflow-auto">
        <DepartmentTree
          selectedId={selectedDeptId}
          onSelect={handleSelectDepartment}
          onAddRoot={handleAddRoot}
          onAddChild={handleAddChild}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </div>

      {/* 详情 */}
      <DepartmentDetail
        department={selectedDepartment}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAddUser={handleAddUser}
      />

      {/* 弹窗 */}
      <CreateDepartmentDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        parentId={selectedParentId}
        onSuccess={handleCreateSuccess}
      />

      <EditDepartmentDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        department={editingDepartment}
      />

      {selectedDepartment && (
        <AddUserDialog
          open={addUserDialogOpen}
          onOpenChange={setAddUserDialogOpen}
          departmentId={selectedDepartment.id}
          departmentName={selectedDepartment.name}
        />
      )}

      <DeleteDepartmentDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        department={deletingDepartment}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  )
}
